import { isRedirectError } from 'next/dist/client/components/redirect-error';
import { usePathname } from 'next/navigation';
import { useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { watchlistStore } from 'src/stores/watchListStore';
import { ProductListDataRow } from 'src/types/CataloguePage.types';
import { DataLayerWatchlistEvent, pushToDataLayerWatchList } from 'src/utils/pushToDataLayerWatchList';

import { ToasterService } from '../../Toaster/ToasterService';
import {
  WATCHLIST_MESSAGES,
  displayAddWatchListItemToast,
  displayRemoveWatchListItemToast,
  getFullRedirectUrl,
} from '../AddToWatchList.helper';
import { toggleWatchListAction } from '../actions/toggleWatchList.action';

export const useToggleWatchList = (
  sku: string,
  disableSnackbar: boolean = false,
  onWatchlistStatusChange?: () => Promise<void>,
  product?: Partial<ProductListDataRow>,
) => {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);
  const { hasWatchlistItem, setWatchlistData } = watchlistStore(
    (state) => ({
      hasWatchlistItem: state.watchlist.has(sku),
      setWatchlistData: state.setWatchlistData,
    }),
    shallow,
  );
  const [optimisticallyAdded, setOptimisticallyAdded] = useState<boolean | undefined>(undefined);

  const userAddedItem = useMemo(
    () => optimisticallyAdded ?? hasWatchlistItem,
    [optimisticallyAdded, sku, hasWatchlistItem],
  );

  const handleError = (error: any) => {
    if (isRedirectError(error)) return;
    setOptimisticallyAdded(false);
    if (disableSnackbar) return;

    const status = error?.response?.status;
    if (status === 409) {
      ToasterService.error({ message: WATCHLIST_MESSAGES.addToWatchlistTooManyErrorMessage }, { autoClose: 5000 });
    } else {
      ToasterService.error({ message: WATCHLIST_MESSAGES.addToWatchlistGenericErrorMessage }, { autoClose: 5000 });
    }
  };

  const onUndo = () => {
    toggleItem(false);
    pushToDataLayerWatchList(DataLayerWatchlistEvent.watchlist_undos, { sku });
  };

  const handleToggle = () => {
    toggleItem(userAddedItem);
  };

  const toggleItem = async (isAdded: boolean) => {
    const shouldAdd = !isAdded;
    setOptimisticallyAdded(shouldAdd);

    try {
      setIsLoading(true);
      const result = await toggleWatchListAction(sku, isAdded, getFullRedirectUrl(sku, pathname ?? ''));
      if (!result) {
        pushToDataLayerWatchList(DataLayerWatchlistEvent.watchlist_add_attempt, { sku });
        return;
      }

      setWatchlistData(result ?? null);
      if (onWatchlistStatusChange) await onWatchlistStatusChange();
      if (shouldAdd) {
        pushToDataLayerWatchList(DataLayerWatchlistEvent.watchlist_successful_add, { sku, product });
      } else {
        pushToDataLayerWatchList(DataLayerWatchlistEvent.watchlist_remove, { sku });
      }

      if (!disableSnackbar) {
        shouldAdd ? displayAddWatchListItemToast() : displayRemoveWatchListItemToast(onUndo);
      }
      setIsLoading(false);
    } catch (error) {
      handleError(error);
    }
  };

  return {
    handleToggle,
    userAddedItem,
    isLoading,
  };
};
